import React, { useEffect, useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { getLocation } from '@/lib/geo';
import { visibleInterval } from '@/lib/visibleInterval';

const OK_KEY = 'rtr_last_location';
const FAIL_KEY = 'rtr_location_fail';
const RETRY_MS = 30 * 1000;
const readNum = k => Number(localStorage.getItem(k) || 0);
const fmt = ms => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export const markLocationSent = () => { localStorage.setItem(OK_KEY, String(Date.now())); localStorage.removeItem(FAIL_KEY); };

export function LocationGuard({ api, session, onTimeout }) {
  const intervalMs = (session?.location_interval_minutes || 15) * 60 * 1000;
  const graceMs = (session?.location_grace_minutes || 5) * 60 * 1000;
  const [left, setLeft] = useState(null);
  const [msg, setMsg] = useState('');
  const busy = useRef(false);
  const lastTry = useRef(0);
  const fired = useRef(false);

  useEffect(() => {
    if (session?.needs_device_adopt) api.post('/auth/device/adopt').catch(() => {});
  }, [api, session?.needs_device_adopt]);

  useEffect(() => {
    if (!session?.location_required) return undefined;
    const attempt = async () => {
      busy.current = true;
      lastTry.current = Date.now();
      try {
        const loc = await getLocation(20000);
        await api.post('/auth/location', loc);
        markLocationSent();
      } catch (e) {
        if (!readNum(FAIL_KEY)) localStorage.setItem(FAIL_KEY, String(Date.now()));
        setMsg(e.message);
      } finally { busy.current = false; }
    };
    const tick = () => {
      const t = Date.now();
      const due = t - readNum(OK_KEY) >= intervalMs;
      const failAt = readNum(FAIL_KEY);
      if (due && !busy.current && t - lastTry.current >= RETRY_MS) attempt();
      if (failAt) {
        const remaining = failAt + graceMs - t;
        if (remaining <= 0 && !fired.current) { fired.current = true; onTimeout('location'); return; }
        setLeft(remaining);
      } else setLeft(null);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [api, session?.location_required, intervalMs, graceMs, onTimeout]);

  if (left == null) return null;
  return (
    <div className="fixed inset-0 z-[9998] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="location-warning-overlay">
      <div className="bg-white rounded-md shadow-xl max-w-sm w-full p-6 border border-[#E2E2DF]">
        <div className="flex items-center gap-2 text-[#D35400] mb-2"><MapPin size={18} /><h3 className="font-semibold text-slate-900">Location needed</h3></div>
        <p className="text-sm text-slate-600 mb-2">{msg}</p>
        <p className="text-sm text-slate-600">You'll be logged out in <b data-testid="location-warning-countdown">{fmt(left)}</b> unless location sharing comes back.</p>
        <button data-testid="location-warning-retry-btn" onClick={() => { lastTry.current = 0; }} className="mt-4 px-4 py-2 text-sm rounded-md bg-[#D35400] hover:bg-[#A04000] text-white font-semibold transition-colors">Try again</button>
      </div>
    </div>
  );
}

export function AdminDeviceAlert({ api }) {
  const seen = useRef(0);
  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get('/admin/devices/pending-count');
        if (data.count > seen.current) {
          toast.warning(`${data.count} device${data.count > 1 ? 's' : ''} waiting for approval`, {
            id: 'device-approval', duration: 15000,
            action: { label: 'Review', onClick: () => { window.location.href = '/admin/security'; } },
          });
        }
        seen.current = data.count;
      } catch { /* not fatal */ }
    };
    check();
    return visibleInterval(check, 30000);
  }, [api]);
  return null;
}
